import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import api from '../../utils/api'
import ConfirmModal from '../../components/modals/ConfirmModal'

interface Project {
    _id: string; 
    name: string; 
    location: string; 
    priceRange?: string; 
    status?: string;
    projectStatus?: string; 
    projectType: string; 
    configuration?: string; 
    isActive: boolean; 
    createdAt: string;
    overview?: {
        priceRange?: string;
        configuration?: string;
    };
    developer?: { name: string; logo: string };
}

export default function AdminDrafts() {
    const { token } = useAuth()
    const [projects, setProjects] = useState<Project[]>([])
    const [loading, setLoading] = useState(true)
    const [deleting, setDeleting] = useState<string | null>(null)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [search, setSearch] = useState('')
    const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean; id: string; name: string }>({ isOpen: false, id: '', name: '' });

    useEffect(() => {
        if (!token) { setLoading(false); return }
        api.adminGetAllProjects(token)
            .then(data => { 
                if (Array.isArray(data)) {
                    // Filter for Drafts only
                    setProjects(data.filter(p => p.status === 'Draft'))
                } 
            })
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [token])

    const handleDelete = async () => {
        const { id } = confirmModal;
        if (!token || !id) return
        setDeleting(id)
        setConfirmModal({ ...confirmModal, isOpen: false });
        try {
            const res = await api.adminDeleteProject(token, id)
            if (res.message === 'Project deleted') {
                setProjects(prev => prev.filter(p => p._id !== id))
                setMsg({ type: 'success', text: `Draft deleted successfully.` })
            } else {
                setMsg({ type: 'error', text: res.message || 'Delete failed.' })
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Server error failed to delete draft.' })
        }
        setDeleting(null)
    }

    const filtered = projects.filter(p =>
        (p.name || '').includes(search) ||
        (p.location || '').includes(search) ||
        (p.developer?.name || '').includes(search)
    )

    return (
        <AdminLayout>
            <div className="animate__animated animate__fadeIn">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Project Drafts</h1>
                        <p className="text-base font-medium text-slate-500">Manage and continue editing your unpublished project drafts.</p>
                    </div>
                    <Link to="/admin-add-project" className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-slate-900 text-slate-900 hover:bg-slate-50 text-sm font-bold rounded-xl transition-all shadow-md active:scale-95">
                        <i className="fa-solid fa-plus"></i>
                        Start New Project
                    </Link>
                </div>

                {/* Notifications */}
                {msg && (
                    <div className={`mb-8 p-4 rounded-xl border-2 animate__animated animate__slideInDown shadow-sm ${msg.type === 'success' ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-rose-50 border-rose-100 text-rose-700'}`}>
                        <div className="flex items-center gap-4 font-bold text-sm">
                            <i className={`fa-solid ${msg.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'} text-lg`}></i>
                            {msg.text}
                        </div>
                    </div>
                )}

                {/* Main Table Container */}
                <div className="bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-sm overflow-hidden">
                    {/* Table Toolbar */}
                    <div className="p-6 md:p-8 border-b border-blue-900/10 dark:border-slate-800 flex flex-col lg:flex-row lg:items-center justify-between gap-6 bg-white/50 dark:bg-slate-800/30">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white border-2 border-slate-900 text-slate-900 rounded-xl flex items-center justify-center shadow-lg shadow-slate-100">
                                <i className="fa-solid fa-pen-ruler text-lg"></i>
                            </div>
                            <div>
                                <h5 className="text-xl font-bold text-slate-900 dark:text-white">Unfinished Drafts</h5>
                                <p className="text-sm font-semibold text-slate-500 mt-0.5">{filtered.length} drafts pending</p>
                            </div>
                        </div>

                        <div className="relative group max-w-lg w-full">
                            <i className="fa-solid fa-magnifying-glass absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-slate-900 transition-colors"></i>
                            <input
                                type="text"
                                className="block w-full pl-14 pr-6 py-3.5 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl text-base font-medium focus:ring-2 focus:ring-slate-900 transition-all text-slate-900 placeholder:text-slate-400 shadow-sm"
                                placeholder="Search your drafts..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-32">
                            <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin mb-6"></div>
                            <p className="text-slate-500 font-bold text-sm">Loading drafts...</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-32 px-6 text-center">
                            <div className="w-20 h-20 bg-slate-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-6 border border-slate-100 dark:border-slate-700 shadow-sm">
                                <i className="fa-solid fa-file-signature text-3xl text-slate-300"></i>
                            </div>
                            <h5 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                                {search ? `No results for "${search}"` : 'No Drafts Found'}
                            </h5>
                            <p className="text-slate-500 text-base font-medium max-w-xs">Start by creating a new project to see it in your drafts.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto scrollbar-hide">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50 dark:bg-slate-800/50">
                                        <th className="px-8 py-5 text-xs font-bold text-slate-500 uppercase tracking-wider">Draft Details</th>
                                        <th className="px-6 py-5 text-xs font-bold text-slate-500 uppercase tracking-wider">Type</th>
                                        <th className="px-6 py-5 text-xs font-bold text-slate-500 uppercase tracking-wider">Last Saved</th>
                                        <th className="px-6 py-5 text-xs font-bold text-slate-500 uppercase tracking-wider">Developer</th>
                                        <th className="px-8 py-5 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100/50 dark:divide-slate-800/50">
                                    {filtered.map(p => {
                                        const lastSaved = new Date(p.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                                        
                                        return (
                                            <tr key={p._id} className="hover:bg-white/80 dark:hover:bg-slate-800/30 transition-all group">
                                                <td className="px-8 py-6 whitespace-nowrap">
                                                     <div className="flex items-center gap-4">
                                                        <div className="w-12 h-12 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 flex items-center justify-center text-slate-400 group-hover:bg-slate-900 group-hover:text-white transition-all shadow-sm">
                                                            <i className="fa-solid fa-file-pen text-base"></i>
                                                        </div>
                                                        <div>
                                                            <div className="text-base font-bold text-slate-900 dark:text-white tracking-tight group-hover:translate-x-1 transition-transform">{p.name || 'Untitled Draft'}</div>
                                                            <div className="text-sm font-medium text-slate-500 mt-0.5">{p.location || 'Location not specified'}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-6 whitespace-nowrap">
                                                    <span className="text-xs font-bold text-slate-600 bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200">{p.projectType || 'N/A'}</span>
                                                </td>
                                                <td className="px-6 py-6 whitespace-nowrap">
                                                    <div className="text-sm font-bold text-slate-900">{lastSaved}</div>
                                                </td>
                                                <td className="px-6 py-6 whitespace-nowrap">
                                                    <div className="flex items-center gap-2.5">
                                                        <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center border border-slate-100">
                                                            <i className="fa-solid fa-building-columns text-xs text-slate-400"></i>
                                                        </div>
                                                        <span className="text-sm font-bold text-slate-900">{p.developer?.name || 'Private owner'}</span>
                                                    </div>
                                                </td>
                                                 <td className="px-8 py-6 whitespace-nowrap text-right">
                                                    <div className="flex justify-end items-center gap-3">
                                                        <Link to={`/admin-edit-project/${p._id}`} className="px-5 py-2.5 rounded-xl bg-white border-2 border-slate-900 text-slate-900 hover:bg-slate-50 font-bold text-xs transition-all shadow-md active:scale-95 flex items-center gap-2">
                                                            <i className="fa-solid fa-play text-[10px]"></i>
                                                            Resume Editing
                                                        </Link>
                                                        <button
                                                            disabled={deleting === p._id}
                                                            onClick={() => setConfirmModal({ isOpen: true, id: p._id, name: p.name })}
                                                            className="w-10 h-10 rounded-xl bg-white border-2 border-slate-100 text-slate-400 hover:border-rose-600 hover:text-rose-600 transition-all flex items-center justify-center shadow-sm disabled:opacity-50 active:scale-90"
                                                            title="Delete Draft"
                                                        >
                                                            {deleting === p._id
                                                                ? <div className="w-5 h-5 border-2 border-rose-600 border-t-transparent rounded-full animate-spin"></div>
                                                                : <i className="fa-solid fa-trash-can text-sm"></i>
                                                            }
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <ConfirmModal 
                isOpen={confirmModal.isOpen}
                title="Delete Draft"
                message={`Are you sure you want to permanently delete the draft "${confirmModal.name}"? This action cannot be undone.`}
                onConfirm={handleDelete}
                onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                confirmText="Delete permanently"
                cancelText="Keep draft"
                type="danger"
            />
        </AdminLayout>
    )
}
